import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Button, Container, Row, Col, Card, Alert, Table } from "react-bootstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { serverHost } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";

const DialplanEdit = ({ method }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    hostname: "",
    context: "",
    continue: "false",
    order: "",
    destination: "false",
    domain: "",
    enabled: 'true',
    description: "",
  });

  const [tableRows, setTableRows] = useState([
    {
      tag: "",
      type: "",
      data: "",
      break: "false",
      inline: "",
      group: 0,
      order: 10,
      enabled: 'true'
    }
  ]);

  const [domainList, setDomainList] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch domains on component mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await fetch(`${serverHost}/get_rows?table=v_domains&pageSize=50&page=1`);
        if (res.ok) {
          const data = await res.json();
          const domains = (data.results || []).map(r => ({
            domain_uuid: r.domain_uuid,
            domain_name: r.domain_name,
          }));
          setDomainList(domains);
        }
      } catch (err) {
        console.error("Failed to fetch domains:", err);
      }
    };

    const fetchDialplan = async () => {
      try {
        let res = await fetch(`${serverHost}/get_row?table=v_dialplans&pk=dialplan_uuid&id=${encodeURIComponent(id)}`);
        let data = null;
        if (res.ok) {
          data = await res.json();
          const route = data.result
          console.log(route)
          setFormData({
            name: route.dialplan_name || "",
            number: route.dialplan_number || "",
            hostname: route.dialplan_hostname || "",
            context: route.dialplan_context || "",
            continue: route.dialplan_continue || "false",
            order: route.dialplan_order || "",
            destination: route.dialplan_destination || "false",
            domain: route.domain_uuid || "",
            enabled: route.dialplan_enabled !== undefined ? route.dialplan_enabled : true,
            description: route.dialplan_description || "",
          });

          if (data.tableRows && data.tableRows.length > 0) {
            setTableRows(data.tableRows);
          }
        }
        res = await fetch(`${serverHost}/get_actions/${encodeURIComponent(id)}`)
        
        if (res.ok) {
          data = await res.json()
          const actions = data.result;
          console.log(actions)
          const actionRows = actions.map((a) => ({
            tag: a.dialplan_detail_tag,
            type: a.dialplan_detail_type,
            data: a.dialplan_detail_data,
            break: a.dialplan_detail_break,
            inline: a.dialplan_detail_inline,
            group: a.dialplan_detail_group,
            order: a.dialplan_detail_order,
            enabled: a.dialplan_detail_enabled
          }))
          console.log(actionRows)
          setTableRows(actionRows);
        }
      } catch (err) {
        console.error("Failed to fetch inbound route:", err);
        setError("Failed to load inbound route data");
      } finally {
        setLoading(false);
      }
    };

    fetchDomains();
    if (id) {
      fetchDialplan();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addTableRow = () => {
    const lastRow = tableRows[tableRows.length - 1];
    const newOrder = lastRow ? Number(lastRow.order) + 10 : 10;

    setTableRows([
      ...tableRows,
      {
        tag: "",
        type: "",
        data: "",
        break: "false",
        inline: "",
        group: 0,
        order: newOrder,
        enabled: true
      }
    ]);
  };

  const removeTableRow = (index) => {
    if (tableRows.length > 1) {
      setTableRows(tableRows.filter((_, i) => i !== index));
    }
  };

  const updateTableRow = (index, field, value) => {
    const updated = [...tableRows];
    updated[index][field] = value;
    setTableRows(updated);
  };

  const handleSave = async () => {
    // Validate required fields
    const missingRequired = [];
    if (!formData.name) missingRequired.push("name");

    if (missingRequired.length) {
      setError(`Missing required fields: ${missingRequired.join(", ")}`);
      return;
    }

    setError("");

    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));

      const payload = {
        data: {
          ...formData,
          tableRows: tableRows,
        },
        client_uuid: site.client_uuid,
        site_name: site.company_name,
        site_uuid: site.site_uuid,
        update_uuid: JSON.parse(localStorage.getItem("user")).user_uuid
      };

      const res = await fetch(`${serverHost}/update_dialplan/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to update record");
      }

      navigate(`/admin/hucs/${method}-routes`);
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
  };

  const handleBack = () => {
    const isDirty = Object.values(formData).some(
      (val) => val !== "" && val !== null
    );

    if (!isDirty || window.confirm("Discard changes and go back?")) {
      navigate(`/admin/hucs/${method}-routes`);
    }
  };

  const labelStyle = {
    fontWeight: "bolder",
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: '0.5rem'
  };

  if (loading) {
    return (
      <>
        <SimpleHeader name="Edit Inbound Route" parentName="HUCS" />
        <Container className="mt--6">
          <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
            <Card.Body className="p-4 text-center">
              <p>Loading<span className="loading-dots"></span></p>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <SimpleHeader name="Edit Inbound Route" parentName="HUCS" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header style={{
            backgroundColor: isDark ? "#1f2937" : "#e9ecef",
            color: isDark ? "#e2e8f0" : "#1e3a5f",
            borderBottom: 'none',
            padding: '1rem 1.5rem'
          }}>
            <h4 className="mb-0">EDIT {method.toUpperCase()} ROUTE</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form className="edit-form">
              {/* Basic Fields Section */}
              <div className="mb-4">
                <h5 className="section-title mb-3" style={{ fontSize: "1.5rem", fontWeight: "700" }}>
                  Basic Information
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Name <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}                        
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.number}
                        name="number"
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hostname</Form.Label>
                      <Form.Control
                        type="text"
                        name="hostname"
                        value={formData.hostname}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Context</Form.Label>
                      <Form.Control
                        type="text"
                        name="context"
                        value={formData.context}
                        onChange={handleChange}
                        disabled
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          paddingTop: "1.5rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                          Continue
                        </span>

                        <label className="custom-toggle mb-0">
                          <input
                            type="checkbox"
                            id="continue"
                            name="continue"
                            checked={formData.continue === true || formData.continue === "true"}
                            onChange={handleChange}
                          />
                          <span
                            className="custom-toggle-slider rounded-circle"
                            data-label-off="Off"
                            data-label-on="On"
                          />
                        </label>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={9}>
                    <Form.Group className="mb-3">
                      <Form.Label>Order</Form.Label>
                      <Form.Control
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          paddingTop: "1.5rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                          Destination
                        </span>

                        <label className="custom-toggle mb-0">
                          <input
                            type="checkbox"
                            id="destination"
                            name="destination"
                            checked={formData.destination === true || formData.destination  === "true"}
                            onChange={handleChange}
                          />
                          <span
                            className="custom-toggle-slider rounded-circle"
                            data-label-off="Off"
                            data-label-on="On"
                          />
                        </label>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={9}>
                    <Form.Group className="mb-3">
                      <Form.Label>Domain</Form.Label>
                      <Input
                        type="select"
                        name="domain"
                        value={formData.domain}
                        onChange={handleChange}
                        disabled
                      >
                        <option value="">Select domain...</option>
                        {domainList.map((domain) => (
                          <option key={domain.domain_uuid} value={domain.domain_uuid}>
                            {domain.domain_name}
                          </option>
                        ))}
                      </Input>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        paddingTop: "1.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                        Enabled
                      </span>

                      <label className="custom-toggle mb-0">
                        <input
                          type="checkbox"
                          id="enabled"
                          name="enabled"
                          checked={formData.enabled === true || formData.enabled === "true"}
                          onChange={handleChange}
                        />
                        <span
                          className="custom-toggle-slider rounded-circle"
                          data-label-off="Off"
                          data-label-on="On"
                        />
                      </label>
                    </div>
                  </Col>

                  <Col md={9}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        rows={2}
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Table Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="section-title mb-0" style={{ fontSize: "1.5rem", fontWeight: "700" }}>
                    Route Details
                  </h5>
                  <Button
                    variant="success"
                    onClick={addTableRow}
                    size="sm"
                    className="d-flex align-items-center gap-1"
                  >
                    + Add Row
                  </Button>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <Table bordered hover responsive>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Tag</th>
                        <th>Type</th>
                        <th>Data</th>
                        <th>Break</th>
                        <th>Inline</th>
                        <th>Group</th>
                        <th>Order</th>
                        <th>Enabled</th>
                        <th style={{ width: "80px" }}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row, index) => (
                        <tr key={index}>
                          <td className="text-center align-middle">
                            <Input
                              type="select"
                              size="sm"
                              value={row.tag}
                              onChange={(e) => updateTableRow(index, "tag", e.target.value)}
                              style={{minWidth: "100px" }}
                            >
                                <option></option>
                                <option value="condition">Condition</option>
                                <option value="regex">Regex</option>
                                <option value="action">Action</option>
                                <option value="anti-action">Anti-Action</option>
                            </Input>
                          </td>
                          <td className="text-center align-middle">
                            <Input
                                type="select"
                                size="sm"
                                value={row.type}
                                onChange={(e) => updateTableRow(index, "type", e.target.value)}
                                style={{minWidth: "100px" }}
                            >
                                
                                <option value=""></option>
                                <optgroup label="Condition or Regex">
                                  <option value="ani">ANI</option>
                                  <option value="ani2">ANI2</option>
                                  <option value="caller_id_name">Caller ID Name</option>
                                  <option value="caller_id_number">Caller ID Number</option>
                                  <option value="chan_name">Channel Name</option>
                                  <option value="context">Context</option>
                                  <option value="destination_number">Destination Number</option>
                                  <option value="dialplan">Dialplan</option>
                                  <option value="network_addr">Network Address</option>
                                  <option value="rdnis">RDNIS</option>
                                  <option value="source">Source</option>
                                  <option value="username">Username</option>
                                  <option value="uuid">UUID</option>
                                  <option value="${call_direction}">{"${call_direction}"}</option>
                                  <option value="${number_alias}">{"${number_alias}"}</option>
                                  <option value="${sip_contact_host}">{"${sip_contact_host}"}</option>
                                  <option value="${sip_contact_uri}">{"${sip_contact_uri}"}</option>
                                  <option value="${sip_contact_user}">{"${sip_contact_user}"}</option>
                                  <option value="${sip_h_Diversion}">{"${sip_h_Diversion}"}</option>
                                  <option value="${sip_from_host}">{"${sip_from_host}"}</option>
                                  <option value="${sip_from_uri}">{"${sip_from_uri}"}</option>
                                  <option value="${sip_from_user}">{"${sip_from_user}"}</option>
                                  <option value="${sip_to_uri}">{"${sip_to_uri}"}</option>
                                  <option value="${sip_to_user}">{"${sip_to_user}"}</option>
                                  <option value="${toll_allow}">{"${toll_allow}"}</option>
                                </optgroup>
                                <optgroup label="Applications">
                                  <option value="acknowledge_call">acknowledge_call</option>
                                  <option value="answer">answer</option>
                                  <option value="att_xfer">att_xfer</option>
                                  <option value="bind_digit_action">bind_digit_action</option>
                                  <option value="bind_meta_app">bind_meta_app</option>
                                  <option value="block_dtmf">block_dtmf</option>
                                  <option value="break">break</option>
                                  <option value="bridge">bridge</option>
                                  <option value="bridge_export">bridge_export</option>
                                  <option value="broadcast">broadcast</option>
                                  <option value="callcenter">callcenter</option>
                                  <option value="callcenter_track">callcenter_track</option>
                                  <option value="capture">capture</option>
                                  <option value="capture_text">capture_text</option>
                                  <option value="check_acl">check_acl</option>
                                  <option value="clear_digit_action">clear_digit_action</option>
                                  <option value="clear_speech_cache">clear_speech_cache</option>
                                  <option value="cng_plc">cng_plc</option>
                                  <option value="conference">conference</option>
                                  <option value="conference_set_auto_outcall">conference_set_auto_outcall</option>
                                  <option value="curl">curl</option>
                                  <option value="curl_sendfile">curl_sendfile</option>
                                  <option value="db">db</option>
                                  <option value="debug_media">debug_media</option>
                                  <option value="decode_video">decode_video</option>
                                  <option value="deduplicate_dtmf">deduplicate_dtmf</option>
                                  <option value="deflect">deflect</option>
                                  <option value="delay_echo">delay_echo</option>
                                  <option value="detect_audio">detect_audio</option>
                                  <option value="detect_silence">detect_silence</option>
                                  <option value="detect_speech">detect_speech</option>
                                  <option value="digit_action_set_realm">digit_action_set_realm</option>
                                  <option value="displace_session">displace_session</option>
                                  <option value="early_hangup">early_hangup</option>
                                  <option value="eavesdrop">eavesdrop</option>
                                  <option value="echo">echo</option>
                                  <option value="enable_heartbeat">enable_heartbeat</option>
                                  <option value="enable_keepalive">enable_keepalive</option>
                                  <option value="endless_playback">endless_playback</option>
                                  <option value="enum">enum</option>
                                  <option value="eval">eval</option>
                                  <option value="event">event</option>
                                  <option value="execute_extension">execute_extension</option>
                                  <option value="export">export</option>
                                  <option value="fax_detect">fax_detect</option>
                                  <option value="fifo">fifo</option>
                                  <option value="fifo_track_call">fifo_track_call</option>
                                  <option value="filter_codecs">filter_codecs</option>
                                  <option value="final">final</option>
                                  <option value="fire">fire</option>
                                  <option value="flush_dtmf">flush_dtmf</option>
                                  <option value="gentones">gentones</option>
                                  <option value="group">group</option>
                                  <option value="hangup">hangup</option>
                                  <option value="hash">hash</option>
                                  <option value="hold">hold</option>
                                  <option value="info">info</option>
                                  <option value="intercept">intercept</option>
                                  <option value="ivr">ivr</option>
                                  <option value="jitterbuffer">jitterbuffer</option>
                                  <option value="limit">limit</option>
                                  <option value="limit_execute">limit_execute</option>
                                  <option value="limit_hash">limit_hash</option>
                                  <option value="limit_hash_execute">limit_hash_execute</option>
                                  <option value="log">log</option>
                                  <option value="loop_playback">loop_playback</option>
                                  <option value="lua">lua</option>
                                  <option value="media_reset">media_reset</option>
                                  <option value="mkdir">mkdir</option>
                                  <option value="msrp_recv_file">msrp_recv_file</option>
                                  <option value="msrp_send_file">msrp_send_file</option>
                                  <option value="multiset">multiset</option>
                                  <option value="multiunset">multiunset</option>
                                  <option value="mutex">mutex</option>
                                  <option value="native_eavesdrop">native_eavesdrop</option>
                                  <option value="novideo">novideo</option>
                                  <option value="park">park</option>
                                  <option value="park_state">park_state</option>
                                  <option value="phrase">phrase</option>
                                  <option value="pickup">pickup</option>
                                  <option value="play_and_detect_speech">play_and_detect_speech</option>
                                  <option value="play_and_get_digits">play_and_get_digits</option>
                                  <option value="play_fsv">play_fsv</option>
                                  <option value="play_yuv">play_yuv</option>
                                  <option value="playback">playback</option>
                                  <option value="pre_answer">pre_answer</option>
                                  <option value="preprocess">preprocess</option>
                                  <option value="presence">presence</option>
                                  <option value="privacy">privacy</option>
                                  <option value="push">push</option>
                                  <option value="queue_dtmf">queue_dtmf</option>
                                  <option value="read">read</option>
                                  <option value="record">record</option>
                                  <option value="record_fsv">record_fsv</option>
                                  <option value="record_session">record_session</option>
                                  <option value="record_session_mask">record_session_mask</option>
                                  <option value="record_session_pause">record_session_pause</option>
                                  <option value="record_session_resume">record_session_resume</option>
                                  <option value="record_session_unmask">record_session_unmask</option>
                                  <option value="recovery_refresh">recovery_refresh</option>
                                  <option value="redirect">redirect</option>
                                  <option value="remove_bugs">remove_bugs</option>
                                  <option value="rename">rename</option>
                                  <option value="reply">reply</option>
                                  <option value="respond">respond</option>
                                  <option value="reuse_caller_profile">reuse_caller_profile</option>
                                  <option value="ring_ready">ring_ready</option>
                                  <option value="rxfax">rxfax</option>
                                  <option value="say">say</option>
                                  <option value="sched_broadcast">sched_broadcast</option>
                                  <option value="sched_cancel">sched_cancel</option>
                                  <option value="sched_hangup">sched_hangup</option>
                                  <option value="sched_heartbeat">sched_heartbeat</option>
                                  <option value="sched_transfer">sched_transfer</option>
                                  <option value="send">send</option>
                                  <option value="send_display">send_display</option>
                                  <option value="send_dtmf">send_dtmf</option>
                                  <option value="send_info">send_info</option>
                                  <option value="session_loglevel">session_loglevel</option>
                                  <option value="set">set</option>
                                  <option value="set_audio_level">set_audio_level</option>
                                  <option value="set_global">set_global</option>
                                  <option value="set_media_stats">set_media_stats</option>
                                  <option value="set_mute">set_mute</option>
                                  <option value="set_name">set_name</option>
                                  <option value="set_profile_var">set_profile_var</option>
                                  <option value="set_user">set_user</option>
                                  <option value="set_zombie_exec">set_zombie_exec</option>
                                  <option value="sleep">sleep</option>
                                  <option value="socket">socket</option>
                                  <option value="sofia_sla">sofia_sla</option>
                                  <option value="sofia_stir_shaken_vs">sofia_stir_shaken_vs</option>
                                  <option value="soft_hold">soft_hold</option>
                                  <option value="sound_test">sound_test</option>
                                  <option value="spandsp_detect_tdd">spandsp_detect_tdd</option>
                                  <option value="spandsp_inject_tdd">spandsp_inject_tdd</option>
                                  <option value="spandsp_send_tdd">spandsp_send_tdd</option>
                                  <option value="spandsp_start_dtmf">spandsp_start_dtmf</option>
                                  <option value="spandsp_start_fax_detect">spandsp_start_fax_detect</option>
                                  <option value="spandsp_start_tone_detect">spandsp_start_tone_detect</option>
                                  <option value="spandsp_stop_detect_tdd">spandsp_stop_detect_tdd</option>
                                  <option value="spandsp_stop_dtmf">spandsp_stop_dtmf</option>
                                  <option value="spandsp_stop_fax_detect">spandsp_stop_fax_detect</option>
                                  <option value="spandsp_stop_inject_tdd">spandsp_stop_inject_tdd</option>
                                  <option value="spandsp_stop_tone_detect">spandsp_stop_tone_detect</option>
                                  <option value="speak">speak</option>
                                  <option value="start_dtmf">start_dtmf</option>
                                  <option value="start_dtmf_generate">start_dtmf_generate</option>
                                  <option value="stop">stop</option>
                                  <option value="stop_displace_session">stop_displace_session</option>
                                  <option value="stop_dtmf">stop_dtmf</option>
                                  <option value="stop_dtmf_generate">stop_dtmf_generate</option>
                                  <option value="stop_record_session">stop_record_session</option>
                                  <option value="stop_tone_detect">stop_tone_detect</option>
                                  <option value="stop_video_write_overlay">stop_video_write_overlay</option>
                                  <option value="stopfax">stopfax</option>
                                  <option value="strftime">strftime</option>
                                  <option value="t38_gateway">t38_gateway</option>
                                  <option value="three_way">three_way</option>
                                  <option value="tone_detect">tone_detect</option>
                                  <option value="transfer">transfer</option>
                                  <option value="transfer_vars">transfer_vars</option>
                                  <option value="txfax">txfax</option>
                                  <option value="unbind_meta_app">unbind_meta_app</option>
                                  <option value="unblock_dtmf">unblock_dtmf</option>
                                  <option value="unhold">unhold</option>
                                  <option value="unloop">unloop</option>
                                  <option value="unset">unset</option>
                                  <option value="unshift">unshift</option>
                                  <option value="vad_test">vad_test</option>
                                  <option value="valet_park">valet_park</option>
                                  <option value="verbose_events">verbose_events</option>
                                  <option value="video_decode">video_decode</option>
                                  <option value="video_refresh">video_refresh</option>
                                  <option value="video_write_overlay">video_write_overlay</option>
                                  <option value="wait_for_answer">wait_for_answer</option>
                                  <option value="wait_for_silence">wait_for_silence</option>
                                  <option value="wait_for_video_ready">wait_for_video_ready</option>
                                </optgroup>
	
                            </Input>
                          </td>
                          <td className="text-center align-middle">
                            <Form.Control
                                type="text"
                                size="sm"
                                value={row.data}
                                onChange={(e) => updateTableRow(index, "data", e.target.value)}
                                style={{minWidth: "150px" }}
                            />
                          </td>
                          <td className="text-center align-middle">
                            <Input
                              type="select"
                              size="sm"
                              value={row.break}
                              onChange={(e) => updateTableRow(index, "break", e.target.value)}
                              style={{ minWidth: "100px" }}
                            >
                                <option></option>
                                <option value="on-true">On True</option>
                                <option value="on-false">On False</option>
                                <option value="always">Always</option>
                                <option value="never">Never</option>
                            </Input>
                          </td>
                          <td className="text-center align-middle">
                            <Input
                              type="select"
                              size="sm"
                              value={row.inline}
                              onChange={(e) => updateTableRow(index, "inline", e.target.value)}
                              style={{minWidth: "100px" }}
                            >
                                <option></option>
                                <option value="false">False</option>
                                <option value="true">True</option>
                            </Input>
                          </td>
                          <td className="text-center align-middle">
                            <Form.Control
                              type="number"
                              size="sm"
                              value={row.group}
                              onChange={(e) => updateTableRow(index, "group", e.target.value)}
                              style={{minWidth: "100px" }}
                            />
                          </td>
                          <td className="text-center align-middle">
                            <Form.Control
                              type="number"
                              size="sm"
                              value={row.order}
                              onChange={(e) => updateTableRow(index, "order", e.target.value)}
                              style={{minWidth: "80px" }}
                            />
                          </td>
                          <td className="text-center align-middle" style={{ width: '80px' }}>
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                              <input
                                type="checkbox"
                                checked={row.enabled}
                                onChange={(e) =>
                                  updateTableRow(index, "enabled", e.target.checked.toString())
                                }
                                style={{
                                  margin: 0,
                                  cursor: 'pointer',
                                  position: 'relative',
                                  width: '16px',
                                  height: '16px'
                                }}
                              />
                            </div>
                          </td>
                          <td className="text-center align-middle">
                            {tableRows.length > 1 && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeTableRow(index)}
                              >
                                ✕
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 d-flex justify-content-between">
                <Button
                  variant="danger"
                  onClick={handleBack}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={!formData.name}
                >
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DialplanEdit;